import { type RefObject, useEffect } from 'react';

// Module-level flag prevents scroll event cascades between synced cards.
// Tracked reset RAF id so cleanup can cancel it and avoid the flag getting
// stuck at `true` when a card unmounts mid-scroll (e.g. React Strict Mode).
let isSyncScrolling = false;
let resetRafId: number | null = null;

const resetSyncFlag = () => {
	resetRafId = null;
	isSyncScrolling = false;
};

const scheduleReset = () => {
	if (resetRafId !== null) cancelAnimationFrame(resetRafId);
	resetRafId = requestAnimationFrame(resetSyncFlag);
};

const getRelativeOffsetTop = (element: HTMLElement, container: HTMLElement): number => {
	let top = 0;
	let el: HTMLElement | null = element;
	while (el && el !== container) {
		top += el.offsetTop;
		el = el.offsetParent as HTMLElement | null;
	}
	return top;
};

/**
 * Registers a scrollable element as a participant in the global sync-scroll group.
 * When `syncEnabled` and `isOpen` are both true, the element joins the group,
 * initializes to a common scroll position, and propagates scroll events
 * section-by-section to all other participants.
 */
export function useSyncScroll(
	ref: RefObject<HTMLElement | null>,
	syncEnabled: boolean,
	isOpen: boolean
) {
	useEffect(() => {
		const card = ref.current;
		if (!card || !syncEnabled || !isOpen) return;

		const wasAlreadySyncing = document.querySelectorAll('.sync-scroll-card').length > 0;
		card.classList.add('sync-scroll-card');

		const initRaf = requestAnimationFrame(() => {
			const allSynced = Array.from(document.querySelectorAll<HTMLElement>('.sync-scroll-card'));
			isSyncScrolling = true;

			if (wasAlreadySyncing) {
				const lead = allSynced.find((el) => el !== card);
				if (lead) card.scrollTop = lead.scrollTop;
			} else {
				for (const el of allSynced) {
					el.scrollTop = 0;
				}
			}

			scheduleReset();
		});

		const handleScroll = () => {
			if (isSyncScrolling) return;
			isSyncScrolling = true;

			const { scrollTop } = card;
			const sections = Array.from(card.querySelectorAll('[data-section]')) as HTMLElement[];

			let activeSection: HTMLElement | null = null;
			let progress = 0;

			for (let i = 0; i < sections.length; i++) {
				const sectionTop = getRelativeOffsetTop(sections[i], card);
				const nextTop = sections[i + 1]
					? getRelativeOffsetTop(sections[i + 1], card)
					: sectionTop + sections[i].offsetHeight;

				if (scrollTop >= sectionTop && scrollTop < nextTop) {
					activeSection = sections[i];
					const height = nextTop - sectionTop;
					progress = height > 0 ? (scrollTop - sectionTop) / height : 0;
					break;
				}
			}

			if (!activeSection && sections.length > 0) {
				const firstSectionTop = getRelativeOffsetTop(sections[0], card);
				if (scrollTop < firstSectionTop) {
					activeSection = sections[0];
					progress = 0;
				} else {
					activeSection = sections[sections.length - 1];
					progress = 1;
				}
			}

			if (!activeSection) {
				isSyncScrolling = false;
				return;
			}

			const sectionName = activeSection.dataset.section;

			for (const target of document.querySelectorAll<HTMLElement>('.sync-scroll-card')) {
				if (target === card) continue;

				const targetSection = target.querySelector<HTMLElement>(`[data-section="${sectionName}"]`);
				if (!targetSection) continue;

				const targetSections = Array.from(
					target.querySelectorAll('[data-section]')
				) as HTMLElement[];
				const targetIdx = targetSections.indexOf(targetSection);
				const targetTop = getRelativeOffsetTop(targetSection, target);
				const targetNextTop = targetSections[targetIdx + 1]
					? getRelativeOffsetTop(targetSections[targetIdx + 1], target)
					: targetTop + targetSection.offsetHeight;

				target.scrollTop = targetTop + progress * (targetNextTop - targetTop);
			}

			scheduleReset();
		};

		card.addEventListener('scroll', handleScroll);

		return () => {
			cancelAnimationFrame(initRaf);
			card.classList.remove('sync-scroll-card');
			card.removeEventListener('scroll', handleScroll);
			// Cancel any pending reset and clear the flag so other cards aren't blocked
			if (resetRafId !== null) {
				cancelAnimationFrame(resetRafId);
				resetRafId = null;
			}
			isSyncScrolling = false;
		};
	}, [syncEnabled, isOpen, ref]);
}
